/**
 * Shim for `@react-navigation/material-top-tabs`.
 *
 * Renders a tab bar (custom `tabBar` render prop or a simple default) plus the
 * active tab's screen. Provides the `state` / `descriptors` / `navigation`
 * shape that custom tab bars in this app rely on. Non-tab route names are
 * delegated to the parent navigator.
 */
import React, { isValidElement, useContext, useMemo, useRef, useState } from 'react';
import { NavigationContext, RouteContext, FocusContext, type Navigation, type Route } from './native';
import { View, Text, TouchableOpacity } from '../web/react-native';

interface TabScreenConfig {
  name: string;
  component: React.ComponentType<any>;
  initialParams?: object;
  options?: any;
}

export function Screen(_props: {
  name: string;
  component: React.ComponentType<any>;
  initialParams?: object;
  options?: any;
}): null {
  return null;
}

function readScreens(children: React.ReactNode): TabScreenConfig[] {
  const out: TabScreenConfig[] = [];
  React.Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    if (child.type === React.Fragment) {
      out.push(...readScreens((child.props as any).children));
      return;
    }
    const p = child.props as any;
    if (p?.name && p?.component) {
      out.push({ name: p.name, component: p.component, initialParams: p.initialParams, options: p.options });
    }
  });
  return out;
}

function DefaultTabBar({ state, descriptors, navigation }: any) {
  return (
    <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#e0e0e0' }}>
      {state.routes.map((route: any, index: number) => {
        const focused = state.index === index;
        const { options } = descriptors[route.key];
        const label = options?.title ?? route.name;
        return (
          <TouchableOpacity
            key={route.key}
            onPress={() => navigation.navigate(route.name)}
            style={{ flex: 1, alignItems: 'center', paddingVertical: 12, borderBottomWidth: focused ? 2 : 0, borderBottomColor: '#00A3E0' }}
          >
            <Text style={{ color: focused ? '#00A3E0' : '#666', fontWeight: focused ? '600' : '400' }}>{label}</Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

function Navigator({
  children,
  initialRouteName,
  tabBar,
  style,
  tabBarPosition = 'top',
}: {
  children?: React.ReactNode;
  initialRouteName?: string;
  tabBar?: (props: any) => React.ReactNode;
  style?: any;
  tabBarPosition?: 'top' | 'bottom';
  [key: string]: any;
}) {
  const parent = useContext(NavigationContext);

  // Stable routes: keys never change, only params/options update when screens re-render.
  const routesRef = useRef<Route[]>([]);
  const screensRef = useRef<TabScreenConfig[]>([]);

  const latestScreens = readScreens(children);
  const latestNames = latestScreens.map((s) => s.name).join('\0');
  const prevNames = screensRef.current.map((s) => s.name).join('\0');

  if (latestNames !== prevNames) {
    // Screen set changed — create fresh stable routes with fixed keys.
    screensRef.current = latestScreens;
    routesRef.current = latestScreens.map((s) => ({
      key: `tab-${s.name}`,
      name: s.name,
      params: s.initialParams ?? {},
    }));
  } else {
    // Screen set unchanged — just refresh params and options without touching keys.
    latestScreens.forEach((s, i) => {
      routesRef.current[i] = {
        ...routesRef.current[i],
        params: { ...s.initialParams, ...routesRef.current[i].params },
      };
      screensRef.current[i] = { ...screensRef.current[i], options: s.options, component: s.component };
    });
  }

  const screens = screensRef.current;
  const routes = routesRef.current;

  const initialIndex = Math.max(0, routes.findIndex((r) => r.name === initialRouteName));
  const [index, setIndex] = useState(initialIndex === -1 ? 0 : initialIndex);

  const tabNavigation = useMemo(() => {
    const nav: any = {
      ...(parent ?? {}),
      navigate: (name: string, params?: object) => {
        const i = routes.findIndex((r) => r.name === name);
        if (i >= 0) {
          // Merge incoming params into the route so screens always see fresh params.
          if (params) {
            routes[i] = { ...routes[i], params: { ...routes[i].params, ...params } };
          }
          setIndex(i);
        } else {
          parent?.navigate(name, params);
        }
      },
      jumpTo: (name: string) => {
        const i = routes.findIndex((r) => r.name === name);
        if (i >= 0) setIndex(i);
      },
      emit: (_event: { type: string; target?: string }) => ({ defaultPrevented: false }),
      goBack: () => parent?.goBack(),
      getParent: () => parent,
      isFocused: () => true,
      addListener: (_type: string, _cb: (e?: any) => void) => () => {},
      setParams: (params: object) => {
        routes[index] = { ...routes[index], params: { ...routes[index].params, ...params } };
      },
      getState: () => ({ index, routes, type: 'tab', key: 'tab' }),
    };
    return nav as Navigation;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parent, index]);

  const descriptors = useMemo(() => {
    const map: Record<string, any> = {};
    screens.forEach((s, i) => {
      map[routes[i].key] = { options: s.options ?? {}, navigation: tabNavigation, route: routes[i] };
    });
    return map;
  }, [screens, routes, tabNavigation]);

  const position = useMemo(
    () => ({
      interpolate: ({ outputRange }: { inputRange: number[]; outputRange: any[] }) =>
        outputRange?.[index] ?? outputRange?.[0],
    }),
    [index],
  );

  const tabBarProps = { state: { index, routes }, descriptors, navigation: tabNavigation, position };
  const bar = tabBar ? tabBar(tabBarProps) : <DefaultTabBar {...tabBarProps} />;

  const activeRoute: Route = routes[index];
  const ActiveComponent = screens[index]?.component;

  return (
    <View style={[{ flex: 1 }, style]}>
      {tabBarPosition === 'top' ? bar : null}
      <View style={{ flex: 1 }}>
        {ActiveComponent ? (
          <NavigationContext.Provider value={tabNavigation}>
            <RouteContext.Provider value={activeRoute}>
              <FocusContext.Provider value={true}>
                <ActiveComponent navigation={tabNavigation} route={activeRoute} />
              </FocusContext.Provider>
            </RouteContext.Provider>
          </NavigationContext.Provider>
        ) : null}
      </View>
      {tabBarPosition === 'bottom' ? bar : null}
    </View>
  );
}

export function createMaterialTopTabNavigator() {
  return { Navigator, Screen };
}

export const MaterialTopTabBar = DefaultTabBar;

export default { createMaterialTopTabNavigator };
